import { BannerLinkType } from "@/types";

export const SCREEN_TARGET_KINDS = [
  "brands",
  "localBrands",
  "countries",
  "categories",
  "cart",
  "specialItems",
] as const;

export const BANNER_TARGET_KINDS = [
  "product",
  "products",
  "search",
  ...SCREEN_TARGET_KINDS,
  "website",
  "custom",
] as const;

export type ScreenTargetKind = (typeof SCREEN_TARGET_KINDS)[number];

export type BannerTargetKind = (typeof BANNER_TARGET_KINDS)[number];

export interface ProductTarget {
  kind: "product";
  productId: string;
  promoCode: string;
}

export interface ProductsTarget {
  kind: "products";
  categoryId: string;
  subCategoryId: string;
  brandId: string;
  countryId: string;
  promoCode: string;
}

export interface SearchTarget {
  kind: "search";
  searchTerm: string;
  promoCode: string;
}

export interface ScreenTarget {
  kind: ScreenTargetKind;
  promoCode: string;
}

export interface WebsiteTarget {
  kind: "website";
  url: string;
}

export interface CustomTarget {
  kind: "custom";
  link: string;
  linkType: BannerLinkType;
}

export type DeepLinkTarget =
  | ProductTarget
  | ProductsTarget
  | SearchTarget
  | ScreenTarget;

export type BannerTarget = DeepLinkTarget | WebsiteTarget | CustomTarget;

export interface BannerLink {
  link: string;
  linkType: BannerLinkType;
}

type QueryParam = readonly [key: string, value: string];

const DEEP_LINK_SCHEME = "dukkanti://";

const PROMO_PARAM = "promo";

const PRODUCTS_FILTER_PARAMS = {
  categoryId: "category",
  subCategoryId: "subCategory",
  brandId: "brand",
  countryId: "country",
} as const;

const SEARCH_PARAM = "search";

const SCREEN_LINKS: Record<
  ScreenTargetKind,
  { host: string; params: QueryParam[] }
> = {
  brands: { host: "brands", params: [] },
  localBrands: { host: "brands", params: [["localOnly", "true"]] },
  countries: { host: "countries", params: [] },
  categories: { host: "categories", params: [] },
  cart: { host: "cart", params: [] },
  specialItems: { host: "cart", params: [["showSpecialItemsBanner", "true"]] },
};

const parseUrl = (value: string): URL | null => {
  try {
    return new URL(value);
  } catch (error) {
    if (error instanceof TypeError) return null;
    throw error;
  }
};

export const parseHttpUrl = (value: string): URL | null =>
  /^https?:\/\/\S+$/i.test(value) ? parseUrl(value) : null;

export const isHttpUrl = (value: string): boolean =>
  parseHttpUrl(value) !== null;

const toDeepLink = (
  path: string[],
  params: QueryParam[],
  promoCode: string,
): string => {
  const query = [...params, [PROMO_PARAM, promoCode] as const]
    .filter(([, value]) => value !== "")
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

  return `${DEEP_LINK_SCHEME}${path.map(encodeURIComponent).join("/")}${query ? `?${query}` : ""}`;
};

const buildDeepLink = (target: DeepLinkTarget): string => {
  switch (target.kind) {
    case "product":
      return toDeepLink(["products", target.productId], [], target.promoCode);
    case "products":
      return toDeepLink(
        ["products"],
        [
          [PRODUCTS_FILTER_PARAMS.categoryId, target.categoryId],
          [PRODUCTS_FILTER_PARAMS.subCategoryId, target.subCategoryId],
          [PRODUCTS_FILTER_PARAMS.brandId, target.brandId],
          [PRODUCTS_FILTER_PARAMS.countryId, target.countryId],
        ],
        target.promoCode,
      );
    case "search":
      return toDeepLink(
        ["search"],
        [[SEARCH_PARAM, target.searchTerm]],
        target.promoCode,
      );
    default: {
      const screen = SCREEN_LINKS[target.kind];

      return toDeepLink([screen.host], screen.params, target.promoCode);
    }
  }
};

export const buildBannerLink = (target: BannerTarget): BannerLink => {
  switch (target.kind) {
    case "website":
      return { link: target.url, linkType: "web-link" };
    case "custom":
      return { link: target.link, linkType: target.linkType };
    default:
      return { link: buildDeepLink(target), linkType: "deep-link" };
  }
};

const decodeComponent = (value: string): string | null => {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    if (error instanceof URIError) return null;
    throw error;
  }
};

const isPlainValue = (value: string | null): value is string =>
  value !== null && value !== "" && value === value.trim();

const parseQuery = (query: string): Map<string, string> | null => {
  const params = new Map<string, string>();

  for (const pair of query.split("&")) {
    const separatorIndex = pair.indexOf("=");

    if (separatorIndex <= 0) return null;

    const key = decodeComponent(pair.slice(0, separatorIndex));
    const value = decodeComponent(
      pair.slice(separatorIndex + 1).replace(/\+/g, " "),
    );

    if (!isPlainValue(key) || !isPlainValue(value) || params.has(key)) {
      return null;
    }

    params.set(key, value);
  }

  return params;
};

const hasOnlyKeys = (
  params: ReadonlyMap<string, string>,
  keys: readonly string[],
): boolean => [...params.keys()].every((key) => keys.includes(key));

const hasExactParams = (
  params: ReadonlyMap<string, string>,
  expected: QueryParam[],
): boolean =>
  params.size === expected.length &&
  expected.every(([key, value]) => params.get(key) === value);

const matchDeepLink = (
  segments: string[],
  params: ReadonlyMap<string, string>,
  promoCode: string,
): DeepLinkTarget | null => {
  const [host, productId, ...extraSegments] = segments;

  if (extraSegments.length) return null;

  if (productId !== undefined) {
    return host === "products" && params.size === 0
      ? { kind: "product", productId, promoCode }
      : null;
  }

  if (host === "products") {
    return hasOnlyKeys(params, Object.values(PRODUCTS_FILTER_PARAMS))
      ? {
          kind: "products",
          categoryId: params.get(PRODUCTS_FILTER_PARAMS.categoryId) ?? "",
          subCategoryId: params.get(PRODUCTS_FILTER_PARAMS.subCategoryId) ?? "",
          brandId: params.get(PRODUCTS_FILTER_PARAMS.brandId) ?? "",
          countryId: params.get(PRODUCTS_FILTER_PARAMS.countryId) ?? "",
          promoCode,
        }
      : null;
  }

  if (host === "search") {
    const searchTerm = params.get(SEARCH_PARAM);

    return searchTerm !== undefined && params.size === 1
      ? { kind: "search", searchTerm, promoCode }
      : null;
  }

  const screenKind = SCREEN_TARGET_KINDS.find(
    (kind) =>
      SCREEN_LINKS[kind].host === host &&
      hasExactParams(params, SCREEN_LINKS[kind].params),
  );

  return screenKind ? { kind: screenKind, promoCode } : null;
};

const parseDeepLink = (link: string): DeepLinkTarget | null => {
  if (!link.startsWith(DEEP_LINK_SCHEME) || link.includes("#")) return null;

  const location = link.slice(DEEP_LINK_SCHEME.length);
  const queryIndex = location.indexOf("?");
  const path = queryIndex === -1 ? location : location.slice(0, queryIndex);
  const params =
    queryIndex === -1
      ? new Map<string, string>()
      : parseQuery(location.slice(queryIndex + 1));
  const segments = path.split("/").map(decodeComponent);

  if (!params || !segments.every(isPlainValue)) return null;

  const promoCode = params.get(PROMO_PARAM) ?? "";
  const screenParams = new Map(
    [...params].filter(([key]) => key !== PROMO_PARAM),
  );

  return matchDeepLink(segments, screenParams, promoCode);
};

const parseWebsiteLink = (link: string): WebsiteTarget | null =>
  link === link.trim() && isHttpUrl(link)
    ? { kind: "website", url: link }
    : null;

export const parseBannerLink = (
  link: string,
  linkType: BannerLinkType,
): BannerTarget =>
  (linkType === "web-link" ? parseWebsiteLink(link) : parseDeepLink(link)) ?? {
    kind: "custom",
    link,
    linkType,
  };

export const changeBannerTargetKind = (
  target: BannerTarget,
  kind: BannerTargetKind,
): BannerTarget => {
  const promoCode = "promoCode" in target ? target.promoCode : "";

  switch (kind) {
    case "product":
      return { kind, productId: "", promoCode };
    case "products":
      return {
        kind,
        categoryId: "",
        subCategoryId: "",
        brandId: "",
        countryId: "",
        promoCode,
      };
    case "search":
      return { kind, searchTerm: "", promoCode };
    case "website":
      return { kind, url: "" };
    case "custom":
      return { kind, ...buildBannerLink(target) };
    default:
      return { kind, promoCode };
  }
};
